/**
 * PricingRuleDetailPage — custom (non-generic) Pricing Rule detail page.
 *
 * Mirrors CustomerDetailPage: reuses the StandardDetailPage engine (schema-driven
 * CRUD, save, delete, permissions, chat, the registered pricingRuleShopifyView
 * for the main field column) and layers on a bespoke, promotion-native chrome:
 *  - headerMedia        → a reward badge beside the title
 *  - headerSubtitle     → a time-sensitive countdown under the title
 *  - renderAboveMain    → a Promotion Summary "command center" card
 *  - renderRightSidebar → trigger / storefront / validity cards
 *
 * All bespoke content reads straight off the loaded doc (formData) — no extra
 * endpoints. The raw ERPNext form stays reachable via `?generic=1`.
 */

import { StandardDetailPage } from "@/features/doc/StandardDetailPage";
import { useLanguage } from "@/i18n/useLanguage";
import { PricingRuleHeaderBadge } from "./PricingRuleHeaderBadge";
import { PricingRulePromotionSummary } from "./PricingRulePromotionSummary";
import { CouponPerformancePanel } from "./CouponPerformancePanel";
import { PricingRuleDetailSidebar } from "./PricingRuleDetailSidebar";
import { promotionCountdown } from "./pricingRuleDetailModel";

function CountdownSubtitle({ formData }: { formData: Record<string, any> }) {
  const { language } = useLanguage();
  const text = promotionCountdown(formData, undefined, language);
  if (!text) return null;
  return <span className="text-[13px] font-medium text-sky-600 dark:text-sky-400">{text}</span>;
}

export function PricingRuleDetailPage() {
  return (
    <StandardDetailPage
      forcedDoctype="Pricing Rule"
      headerMedia={({ formData }) => <PricingRuleHeaderBadge formData={formData} />}
      headerSubtitle={({ formData }) => <CountdownSubtitle formData={formData} />}
      renderAboveMain={({ formData, docName }) => (
        <>
          <CouponPerformancePanel formData={formData} docName={docName} />
          <PricingRulePromotionSummary formData={formData} docName={docName} />
        </>
      )}
      renderRightSidebar={({ formData, docName }) => (
        <PricingRuleDetailSidebar formData={formData} docName={docName} />
      )}
    />
  );
}
